import { Component, type SyntheticEvent } from "react";
import { pick } from "ramda";
import { observer } from "mobx-react";
import { Stack, TextField, DefaultButton } from "@fluentui/react";
import { action, computed, makeObservable, observable } from "mobx";
import { type DepartmentCreate, type Department } from "../../api";
import { doubleBindString } from "../../utils/double-bind";
import { type FormCreateProps } from "../../utils/form-create-props";
import { createUuid } from "../../utils/uuid";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";

export interface FormCreateDepartmentProps extends FormCreateProps<Department> {}

import { store } from "../../store";

@observer
export class FormCreateDepartment extends Component<FormCreateDepartmentProps> {
    @observable public accessor label = "";

    private validationId = createUuid();

    constructor(props: FormCreateDepartmentProps) {
        super(props);
        makeObservable(this);
    }

    private triggerValidation(): void {
        store.repositoryDepartments.validation.create.updateModel(
            this.validationId,
            this.departmentCreate,
        );
    }

    componentDidMount(): void {
        store.repositoryDepartments.validation.create.initializeModel(
            this.validationId,
            this.departmentCreate,
        );
    }

    @computed private get departmentCreate(): DepartmentCreate {
        return pick(["label"], this as FormCreateDepartment);
    }

    @action.bound
    private async createDepartment(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();
        const department = await store.repositoryDepartments.create(this.departmentCreate);
        this.reset();
        if (this.props.onCreate) {
            this.props.onCreate(department);
        }
    }

    @action.bound private reset(): void {
        this.label = "";
        this.triggerValidation();
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formCreateDepartment.submit.text")}
                validation={store.repositoryDepartments.validation.create}
                validationId={this.validationId}
            />
        );
        return (
            <form onSubmit={this.createDepartment}>
                <Stack tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                    <TextField
                        label={store.i18n.t("formCreateDepartment.label.label")}
                        {...doubleBindString<FormCreateDepartment>(this, "label", () =>
                            this.triggerValidation(),
                        )}
                        required
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryDepartments.validation.create.getFieldValidationState(
                                this.validationId,
                                "label",
                            ),
                        )}
                    />
                </Stack>
                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t("formCreateDepartment.cancel.text")}
                            onClick={this.props.onDialogCancel}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
            </form>
        );
    }
}
